import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Trash2, Users } from 'lucide-react';
import { api, type ChatDetail } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { useChat } from '../chat/ChatContext.tsx';
import { Avatar } from '../components/Avatar.tsx';
import { ChatAvatarPicker } from '../components/ChatAvatarPicker.tsx';
import { formatMembers } from '../lib/format.ts';
import './SettingsScreen.css';
import './NewGroupScreen.css';

/**
 * «Информация о группе»: who is in it, the picture on it, and the two ways out
 * of it — leaving, which anyone may do, and removing somebody, which is the
 * creator's alone. So is the picture: a group's avatar is what everyone in it
 * sees in their chat list, and one member changing it for the rest is the
 * creator's business the same way the roster is.
 *
 * The roster is fetched here rather than carried in the chat list, which holds
 * summaries: a member count is what a row needs, and the names are what only
 * this screen needs.
 */
export function GroupInfoScreen() {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { chats, removeMember } = useChat();

  const [detail, setDetail] = useState<ChatDetail | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!chatId) return;
    api
      .chatDetail(chatId)
      .then((found) => {
        // Nothing here describes a chat of two, and the URL is the only way to
        // ask for one — so it goes back to the conversation it belongs to.
        if (found.kind !== 'group') navigate(`/chat/${chatId}`, { replace: true });
        else setDetail(found);
      })
      .catch(() => navigate('/', { replace: true }));
  }, [chatId, navigate]);

  useEffect(load, [load]);

  // Somebody else's removal — or a picture the creator changed on another
  // device — arrives as a change to the summary in the list, and this screen's
  // copy of the group is what has to catch up.
  const summary = chats.find((chat) => chat.id === chatId) ?? null;
  useEffect(() => {
    if (summary === null || summary.memberCount === null || detail === null) return;
    if (
      summary.memberCount !== detail.members.length ||
      summary.avatarPhotoId !== detail.avatarPhotoId
    ) {
      load();
    }
  }, [summary, detail, load]);

  // Being removed from the group takes the chat out of the list; there is
  // nothing left here to look at.
  useEffect(() => {
    if (chatId && chats.length > 0 && !chats.some((chat) => chat.id === chatId)) {
      navigate('/', { replace: true });
    }
  }, [chatId, chats, navigate]);

  async function remove(userId: string) {
    if (!chatId || busy) return;
    setBusy(true);
    try {
      await removeMember(chatId, userId);
      if (userId === user?.id) navigate('/', { replace: true });
      else load();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="app-frame settings-screen">
      <header className="settings-header settings-header-divided">
        <button
          type="button"
          className="icon-button settings-back"
          onClick={() => navigate(`/chat/${chatId}`)}
          aria-label="Назад"
        >
          <ChevronLeft size={22} />
        </button>
        <h2 className="settings-heading">Информация о группе</h2>
      </header>

      {detail && (
        <div className="group-info-body">
          <div className="group-info-head">
            {/* The people glyph is what a group with no picture on it wears
                everywhere else, so it is what this shows too — the tile here is
                the same tile, several times the size. */}
            <Avatar className="group-info-avatar" photoId={detail.avatarPhotoId}>
              <Users size={30} strokeWidth={1.6} />
            </Avatar>
            <h3 className="group-info-name">{detail.title}</h3>
            <span className="group-info-count">{formatMembers(detail.members.length)}</span>

            {/* The creator's alone, the way the roster below is. */}
            {detail.amCreator && chatId && (
              <ChatAvatarPicker
                chatId={chatId}
                onChanged={(avatarPhotoId) =>
                  setDetail((current) => (current ? { ...current, avatarPhotoId } : current))
                }
              />
            )}
          </div>

          <div className="group-info-label">Участники</div>
          {detail.members.map((member) => (
            <div key={member.id} className="group-member">
              <Avatar
                className="group-pick-avatar"
                photoId={member.avatarPhotoId}
                name={member.displayName}
              />
              <div className="group-member-body">
                <div className="group-member-name">{member.displayName}</div>
                {member.isCreator && <div className="group-member-role">Создатель</div>}
              </div>
              {/* Offered to the creator, and never against themselves: leaving
                  is what a creator does, and it says so at the bottom. */}
              {detail.amCreator && member.id !== user?.id && (
                <button
                  type="button"
                  className="icon-button group-member-remove"
                  onClick={() => void remove(member.id)}
                  disabled={busy}
                  aria-label={`Удалить участника ${member.displayName}`}
                >
                  <Trash2 size={17} />
                </button>
              )}
            </div>
          ))}

          <div className="group-leave-wrap">
            <button
              type="button"
              className="group-leave"
              onClick={() => void remove(user?.id ?? '')}
              disabled={busy || !user}
            >
              Покинуть группу
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
